import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Card from 'react-bootstrap/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImage } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Stack from 'react-bootstrap/Stack';
import Spinner from 'react-bootstrap/Spinner';

import { addSpotMenu, getAllCategories } from 'src/Services/api';
import useMutation from 'src/Hooks/useMutation';
import Notification, { useNotification } from 'src/Components/Notification';


import './styles.scss';

const CATEGORIES = {"Foods": 1, "Drinks": 2};
const REQUIRED_FILEDS = ["name", "price", "category"];

const getImageURL = (image) =>
  image instanceof File ? URL.createObjectURL(image) : image;

const AddMenu = () => {
    const [formValues, setFormValues] = useState({});
    const { id: spotId } = useParams();
    const [categories, setCategories] = useState([]);
    const navigate = useNavigate();
    const { notifyError, notifySuccess, onClose, notification } = useNotification();

    const { setter: addMenuAction, loading: addMenuLoading } = useMutation(addSpotMenu);

    useEffect(() => {
      const fetchCategories = async () => {
        try {
          const response = await getAllCategories();
          setCategories(response.data?.categories);
        } catch (error) {
          console.error('Error fetching spot categories:', error);
        }
      };
      fetchCategories();
    }, [spotId]);

    const handleAddMenu = async (event) => {
      event.preventDefault();
      const newMenuData = new FormData();
      newMenuData.append("spot", spotId);
      Object.entries(formValues).forEach(([fieldName, fieldValue]) => {
        newMenuData.append(fieldName, fieldValue);
      });
      addMenuAction(
        { data: newMenuData },
        () => {
          notifySuccess({ message: "Menu added successfully" });
          document.forms.addMenu.reset();
          setFormValues({})
        },
        () => notifyError({ message: "Error: Menu could not be added" }),
      );
    }

    const cancelAddMenu = () => {
      navigate(`/spot/${spotId}/menu`);
    }

    const onFormValueChange = (event) => {
      if (event.target.name) {
        const fieldValue = event.target.name === "image" ?
          event.target.files && event.target.files[0] : event.target.value;
        setFormValues((values) => {
          return { ...values, [event.target.name]: fieldValue };
        });
      }
    }

    const canSubmit = REQUIRED_FILEDS.every((field) => !!formValues[field]);

    return (
      <Container className="mt-4">
        <Form name="addMenu">
          <Row>
            <Col md={7}>
              <h5 className="mb-4 pt-4">New Menu</h5>
              <Form.Group className="mb-4" controlId='addMenuName'>
                <Form.Control placeholder="Name" name="name" onChange={onFormValueChange} />
              </Form.Group>
              <Form.Group className="mb-4" controlId='addMenuPrice'>
                <Form.Control
                  type="number"
                  placeholder="Price"
                  step="0.01"
                  min="0"
                  name="price"
                  onChange={onFormValueChange}
                />
              </Form.Group>
              <Row className="mb-4">
                <Col>
                  <Form.Select
                    aria-label="Menu Category"
                    name="category"
                    defaultValue=""
                    onChange={onFormValueChange}
                  >
                    <option key="category" value="" hidden>Category</option>
                    {/* {Object.keys(CATEGORIES).map((category) =>
                        <option key={category} value={CATEGORIES[category]}>{category}</option>
                    )} */}
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Select
                    aria-label="Is Menu Available?"
                    name="is_available"
                    onChange={onFormValueChange}
                    defaultValue=""
                  >
                    <option value="" hidden>Available?</option>
                    <option value={true}>Yes</option>
                    <option value={false}>No</option>
                  </Form.Select>
                </Col>
              </Row>
              <Form.Group className="mb-4" controlId='addMenuDescription'>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Description"
                  name="description"
                  onChange={onFormValueChange}
                />
              </Form.Group>
            </Col>
            <Col md={{ span: 4, offset: 1 }}>
              <Card className='mtop-2' >
                <Card.Body>
                  <div className="d-flex flex-column align-items-center">
                    <Form.Group controlId='addMenuImage' className='w-100'>
                      <Form.Control
                        type='file'
                        name="image"
                        onChange={onFormValueChange}
                      />
                    </Form.Group>
                  </div>
                </Card.Body>
                <div className="d-flex flex-column align-items-center">
                  {formValues.image ? (
                    <Card.Img variant="top" src={getImageURL(formValues.image)} className="menu-image" />
                  ) : (
                    <div className="d-flex align-items-center no-image">
                      <FontAwesomeIcon
                        fixedWidth
                        icon={faFileImage}
                        color="gray"
                        size="4x"
                      />
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={{ span: 7 }}>
              <Stack direction="horizontal" className="justify-content-between mt-4 mb-2">
                <Button
                  variant="primary"
                  className="jamlyf-primary-button py-2 col-5 fs-6"
                  type="submit"
                  size="sm"
                  disabled={!canSubmit || addMenuLoading}
                  onClick={handleAddMenu}
                >
                  {addMenuLoading && (
                    <Fragment>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="visually-hidden">Loading...</span>
                    </Fragment>
                  )}
                  Save
                </Button>
                <Button
                  variant="outline-primary"
                  className="jamlyf-outline-button py-2 col-5 me-0 fs-6"
                  size="sm"
                  onClick={cancelAddMenu}
                  disabled={addMenuLoading}
                >
                  Cancel
                </Button>
              </Stack>
            </Col>
          </Row>
        </Form>
        <Notification id="menu-edit" onClose={onClose} {...notification} />
      </Container>
    );
}

export default AddMenu;
